import { LOGO_SRC } from '../components/LenticLogo'
import { buildSymbolFavicon } from './favicon'

/**
 * Explicit, fixed document metadata. Never derived from the project or prompt
 * name — these values are the source of truth and are re-applied on load.
 */
export const PAGE_TITLE = 'Lentic — Assessment for AI-assisted work'

export const PAGE_DESCRIPTION =
  'Lentic runs candidates through an instrumented task where the AI is provided but the source documents are withheld, then produces the auditable record of how each person exercised judgement.'

/** The lockup, used as the share image. */
export const SHARE_IMAGE =
  'https://cdn.magicpatterns.com/uploads/hKyoiRxLvYpWPxftj89JFp/lentic-logo-horizontal-teal-navy.svg'

/**
 * The tab and bookmark icon is the circular ripple symbol isolated from the
 * official logo file — same geometry, proportions and colours, wordmark removed.
 * The full lockup stands in only until that crop resolves.
 */
let symbolFavicon: string | null = null

function upsertMeta(key: 'name' | 'property', value: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${key}="${value}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(key, value)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

/** Strips every existing icon reference so no stale or duplicate icon wins. */
function clearIcons() {
  document.head
    .querySelectorAll(
      'link[rel="icon"], link[rel="shortcut icon"], link[rel="apple-touch-icon"], link[rel="apple-touch-icon-precomposed"], link[rel="mask-icon"]',
    )
    .forEach((node) => node.parentElement?.removeChild(node))
}

function addIcon(rel: string, href: string, attrs: Record<string, string> = {}) {
  const link = document.createElement('link')
  link.setAttribute('rel', rel)
  Object.entries(attrs).forEach(([key, value]) => link.setAttribute(key, value))
  link.setAttribute('href', href)
  document.head.appendChild(link)
}

/** One icon source, referenced everywhere, with no stale links left behind. */
function setIcons(href: string) {
  clearIcons()
  addIcon('icon', href, { type: 'image/svg+xml' })
  addIcon('apple-touch-icon', href)
}

export function applyDocumentMetadata() {
  document.title = PAGE_TITLE
  document.documentElement.lang = 'en-GB'

  upsertMeta('name', 'description', PAGE_DESCRIPTION)

  upsertMeta('property', 'og:type', 'website')
  upsertMeta('property', 'og:site_name', 'Lentic')
  upsertMeta('property', 'og:title', PAGE_TITLE)
  upsertMeta('property', 'og:description', PAGE_DESCRIPTION)
  upsertMeta('property', 'og:image', SHARE_IMAGE)

  upsertMeta('name', 'twitter:card', 'summary_large_image')
  upsertMeta('name', 'twitter:title', PAGE_TITLE)
  upsertMeta('name', 'twitter:description', PAGE_DESCRIPTION)
  upsertMeta('name', 'twitter:image', SHARE_IMAGE)

  upsertMeta('name', 'theme-color', '#f4f1ea')

  setIcons(symbolFavicon ?? LOGO_SRC)

  if (!symbolFavicon) {
    void buildSymbolFavicon().then((href) => {
      if (!href) return
      symbolFavicon = href
      setIcons(href)
    })
  }
}

/**
 * Re-asserts the title and social tags if anything else writes to the head —
 * a platform template, a rename, or a republish. Returns a cleanup function.
 */
export function guardDocumentMetadata(): () => void {
  if (typeof MutationObserver === 'undefined') return () => {}

  let scheduled = false

  const observer = new MutationObserver(() => {
    if (scheduled) return
    if (
      document.title === PAGE_TITLE &&
      document.head.querySelector('meta[property="og:title"]')?.getAttribute('content') ===
        PAGE_TITLE
    ) {
      return
    }

    scheduled = true
    // Re-apply after the current mutation batch, so we never fight mid-write.
    window.requestAnimationFrame(() => {
      scheduled = false
      observer.disconnect()
      applyDocumentMetadata()
      observer.observe(document.head, { childList: true, subtree: true, characterData: true })
    })
  })

  observer.observe(document.head, { childList: true, subtree: true, characterData: true })
  return () => observer.disconnect()
}
